// DeviceVerification — one sheet that walks a device verification from the
// request to "Verified". Each step shows one question and at most one
// primary action; the other method is a quiet link, never a tab.

import { LoaderCircle, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Dialog } from "~/components/Dialog";
import { QrCamera, QrCode } from "~/components/QrVerification";
import {
  SheetHeader,
  VerificationGhostButton,
  VerificationPrimaryButton,
} from "~/components/verification-primitives";
import type {
  QrVerificationSession,
  VerificationRequest,
  VerificationSession,
  VerificationState,
} from "~/lib/matrix";
import { isMobileApp } from "~/lib/platform";
import { ICON_STROKE } from "~/lib/icon";

export interface DeviceVerificationProps {
  /** The live SAS session from `useCrypto()` or `startSasVerification`. */
  session: VerificationSession | null;
  request?: VerificationRequest | null;
  qrSession?: QrVerificationSession | null;
  /** SAS state, already subscribed-to by the parent. */
  state: VerificationState | null;
  qrState?: VerificationState | null;
  /** The parent cancels a still-live session. */
  onClose: () => void;
  /** "They match". */
  onConfirm: () => void;
  onStartSas?: () => Promise<void>;
  onStartQr?: () => Promise<void>;
  onScanQrCode?: (bytes: Uint8Array) => Promise<void>;
  onConfirmQr?: () => Promise<void>;
  initialMode?: "sas" | "qr";
  /** This device scans with its camera rather than showing a code. */
  camera?: boolean;
  /** "They don't match". */
  onReject: () => void;
  /** Sends a fresh request after a stop or failure. */
  onRetry?: () => void;
  failureReason?: string;
  error?: string | null;
}

type Step =
  | "waiting"
  | "qr-scan"
  | "qr-show"
  | "qr-scanned"
  | "qr-confirm"
  | "sas-preparing"
  | "compare"
  | "done"
  | "stopped"
  | "failed";

export function DeviceVerification(props: DeviceVerificationProps) {
  const {
    session,
    request = null,
    qrSession = null,
    state,
    qrState = null,
    onClose,
    onConfirm,
    onReject,
    onStartSas,
    onStartQr,
    onScanQrCode,
    onConfirmQr,
    initialMode = "sas",
    camera = isMobileApp,
    onRetry,
    failureReason,
    error,
  } = props;
  const [mode, setMode] = useState<"sas" | "qr">(initialMode);
  const [startError, setStartError] = useState<string | null>(null);
  const [confirmingQr, setConfirmingQr] = useState(false);
  const autoStarted = useRef<"sas" | "qr" | null>(null);
  const requestReady = request?.state === "ready";
  const liveState = mode === "qr" ? (qrState ?? state) : state;
  const step = deriveStep({
    mode,
    request,
    session,
    qrSession,
    liveState,
    camera,
    hasError: Boolean(error && !request && !session && !qrSession),
  });

  // Emoji started on the other device wins over a camera still waiting
  // here; otherwise both sides sit on different methods and nothing moves.
  const hadSession = useRef(session !== null);
  useEffect(() => {
    const appeared = session !== null && !hadSession.current;
    hadSession.current = session !== null;
    if (appeared && mode === "qr" && !qrSession) setMode("sas");
  }, [mode, qrSession, session]);

  // Choosing a method is the start: emoji begins at once, and a computer
  // shows its code for the phone. Only the phone's camera waits on the user.
  useEffect(() => {
    if (!requestReady || autoStarted.current === mode) return;
    if (mode === "sas" && !session) {
      autoStarted.current = "sas";
      void onStartSas?.().catch((cause: unknown) => setStartError(messageOf(cause)));
    }
    if (mode === "qr" && !qrSession && !camera) {
      autoStarted.current = "qr";
      void onStartQr?.().catch((cause: unknown) => setStartError(messageOf(cause)));
    }
  }, [camera, mode, onStartQr, onStartSas, qrSession, requestReady, session]);

  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (step !== "done") return;
    const timer = window.setTimeout(() => closeRef.current(), VERIFIED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [step]);

  const otherMode = mode === "qr" ? "sas" : "qr";

  if (step === "done") {
    return (
      <Dialog
        labelledBy="device-verification-title"
        onClose={onClose}
        dismissOnBackdrop={false}
        grabber={false}
        width="420px"
        panelStyle={{ maxWidth: "calc(100vw - var(--chat-space-4))" }}
      >
        <h2 id="device-verification-title" style={visuallyHidden}>
          Verified
        </h2>
        <div
          style={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Shield pop />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      labelledBy="device-verification-title"
      onClose={onClose}
      dismissOnBackdrop={false}
      grabber={false}
      width="420px"
      panelStyle={{ maxWidth: "calc(100vw - var(--chat-space-4))" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-6)",
          padding: "var(--chat-space-6) var(--chat-space-8) var(--chat-space-8)",
        }}
      >
        <Header title={TITLES[step]} onClose={onClose} />
        <Body>
          {step === "waiting" || step === "sas-preparing" ? (
            <>
              <Spinner />
              <Quiet>{WAITING_INSTRUCTION}</Quiet>
            </>
          ) : null}
          {step === "qr-scan" && onScanQrCode ? <QrCamera onScan={onScanQrCode} /> : null}
          {step === "qr-show" && qrSession?.qrCodeData ? (
            <QrCode bytes={qrSession.qrCodeData} />
          ) : null}
          {step === "qr-show" && !qrSession ? <Spinner /> : null}
          {step === "qr-scanned" || step === "qr-confirm" ? <Shield /> : null}
          {step === "compare" && session ? <EmojiGrid session={session} /> : null}
          {(step === "stopped" || step === "failed") && (failureReason ?? error) ? (
            <Quiet>{failureReason ?? error}</Quiet>
          ) : null}
          {startError ? <Quiet alert>{startError}</Quiet> : null}
        </Body>
        <Actions>
          {step === "compare" ? (
            <>
              <VerificationPrimaryButton size="md" block onClick={onConfirm}>
                They match
              </VerificationPrimaryButton>
              <VerificationGhostButton size="md" block onClick={onReject}>
                They don&apos;t match
              </VerificationGhostButton>
            </>
          ) : null}
          {step === "qr-confirm" && !confirmingQr ? (
            <>
              <VerificationPrimaryButton
                size="md"
                block
                onClick={() => {
                  setConfirmingQr(true);
                  void onConfirmQr?.().catch(() => setConfirmingQr(false));
                }}
              >
                Yes
              </VerificationPrimaryButton>
              <VerificationGhostButton size="md" block onClick={onReject}>
                No
              </VerificationGhostButton>
            </>
          ) : null}
          {(step === "stopped" || step === "failed") && onRetry ? (
            <VerificationPrimaryButton size="md" block onClick={onRetry}>
              Try again
            </VerificationPrimaryButton>
          ) : null}
          {step === "qr-scan" || step === "qr-show" || step === "sas-preparing" ? (
            <VerificationGhostButton size="md" block onClick={() => setMode(otherMode)}>
              Use another method
            </VerificationGhostButton>
          ) : null}
        </Actions>
      </div>
    </Dialog>
  );
}

const TITLES: Record<Step, string> = {
  waiting: "Check your other device",
  "qr-scan": "Scan the code",
  "qr-show": "Scan this code",
  "qr-scanned": "Check your other device",
  "qr-confirm": "See a green shield?",
  "sas-preparing": "Getting emoji ready",
  compare: "Do these match?",
  done: "It\u2019s you",
  stopped: "Verification stopped",
  failed: "That didn\u2019t work",
};

function deriveStep({
  mode,
  request,
  session,
  qrSession,
  liveState,
  camera,
  hasError,
}: {
  mode: "sas" | "qr";
  camera: boolean;
  request: VerificationRequest | null;
  session: VerificationSession | null;
  qrSession: QrVerificationSession | null;
  liveState: VerificationState | null;
  hasError: boolean;
}): Step {
  if (hasError || liveState === "failed") return "failed";
  if (liveState === "done") return "done";
  if (liveState === "cancelled") return "stopped";
  if (mode === "qr") {
    if (qrSession?.qrCodeData) return liveState === "accepted" ? "qr-confirm" : "qr-show";
    if (qrSession) return "qr-scanned";
  }
  if (mode === "sas" && session) {
    return session.emoji.length > 0 && liveState !== "requested" ? "compare" : "sas-preparing";
  }
  if (request && request.state !== "ready") return "waiting";
  if (mode === "qr") return camera ? "qr-scan" : "qr-show";
  return "sas-preparing";
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Verification could not start.";
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return <SheetHeader id="device-verification-title" title={title} onClose={onClose} />;
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <div
      aria-live="polite"
      style={{
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--chat-space-4)",
      }}
    >
      {children}
    </div>
  );
}

function Actions({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-2)" }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <LoaderCircle
      aria-label="Working"
      size={24}
      strokeWidth={ICON_STROKE}
      className="chat-timeline-spinner"
      style={{ color: "var(--chat-fg-secondary)" }}
    />
  );
}

function Shield({ pop = false }: { pop?: boolean }) {
  const ref = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!pop || !node?.animate) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    node.animate(
      [
        { transform: "scale(0.5)", opacity: 0 },
        { transform: "scale(1.15)", opacity: 1, offset: 0.45 },
        { transform: "scale(0.96)", opacity: 1, offset: 0.75 },
        { transform: "scale(1)", opacity: 1 },
      ],
      { duration: 900, delay: 150, easing: "ease-out", fill: "backwards" },
    );
  }, [pop]);
  return (
    <ShieldCheck
      ref={ref}
      aria-hidden
      size={pop ? 88 : 64}
      strokeWidth={ICON_STROKE}
      style={{ color: "var(--chat-state-success)" }}
    />
  );
}

const VERIFIED_HOLD_MS = 2000;

const WAITING_INSTRUCTION = "Open Nurion Chat on your other device and accept the request there.";

const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

function Quiet({ children, alert }: { children: React.ReactNode; alert?: boolean }) {
  return (
    <p
      role={alert ? "alert" : undefined}
      style={{
        margin: 0,
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-lg-size)",
        lineHeight: "var(--chat-type-body-lg-line)",
        textAlign: "center",
      }}
    >
      {children}
    </p>
  );
}

function EmojiGrid({ session }: { session: VerificationSession }) {
  return (
    <div
      aria-label="Emoji to compare"
      style={{
        display: "flex",
        flexWrap: "wrap",
        maxWidth: 280,
        rowGap: "var(--chat-space-4)",
        columnGap: "var(--chat-space-2)",
        justifyContent: "center",
      }}
    >
      {session.emoji.map((e, i) => (
        <div
          key={`${i}-${e.symbol}`}
          role="group"
          aria-label={e.label}
          style={{
            width: 64,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--chat-space-1)",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 32, lineHeight: "40px" }}>
            {e.symbol}
          </span>
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-xs-size)",
              lineHeight: "var(--chat-type-body-xs-line)",
            }}
          >
            {e.label}
          </span>
        </div>
      ))}
    </div>
  );
}
